import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { Ban } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AutoDeclineCard = ({ getAuthHeader }) => {
  const [cfg, setCfg] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/auto-decline-settings`, getAuthHeader());
      setCfg(res.data);
    } catch { /* ignore */ }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (enabled) => {
    if (enabled && !window.confirm('Switch ON auto-decline?\n\nEVERY new application will be automatically DECLINED 5 minutes after submission, and the client will be notified. Staff can still overturn individual declines manually.')) return;
    try {
      const res = await axios.put(`${API_URL}/admin/auto-decline-settings`, { enabled }, getAuthHeader());
      toast.success(res.data.message);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
  };

  if (!cfg) return null;
  return (
    <Card className={`border-l-4 ${cfg.enabled ? 'border-l-red-500 bg-red-50/30' : 'border-l-slate-300'}`} data-testid="auto-decline-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Ban className={`w-4 h-4 ${cfg.enabled ? 'text-red-600' : 'text-slate-400'}`} />
          Auto-Decline All New Applications
          <Badge variant="outline" className={`text-[10px] border-0 ${cfg.enabled ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-500'}`} data-testid="auto-decline-status">
            {cfg.enabled ? 'ON — declining all incoming' : 'OFF'}
          </Badge>
          {cfg.enabled && cfg.pending_declines > 0 && (
            <Badge variant="outline" className="text-[10px] border-0 bg-amber-100 text-amber-800">{cfg.pending_declines} queued</Badge>
          )}
        </CardTitle>
        <p className="text-xs text-muted-foreground">When ON, every application submitted from that moment is automatically declined 5 minutes after submission (client notified, noted in history as "Auto-declined by System"). Applications already in the pipeline are untouched. Switching OFF releases anything still queued.</p>
      </CardHeader>
      <CardContent className="flex items-center gap-3">
        <Switch checked={cfg.enabled} onCheckedChange={toggle} data-testid="auto-decline-switch" />
        <span className="text-sm font-medium">{cfg.enabled ? 'Declining all incoming applications' : 'Normal processing'}</span>
        {cfg.updated_by && <span className="text-[10px] text-slate-400 ml-auto">Last changed by {cfg.updated_by}{cfg.updated_at ? ` · ${new Date(cfg.updated_at).toLocaleString('en-ZA')}` : ''}</span>}
      </CardContent>
    </Card>
  );
};
